/**
 * Take a payment for an order the server has already created.
 *
 * The browser never decides an amount: it opens Razorpay Checkout for the order the server made, and after
 * the customer pays it hands the three values Razorpay returns to POST /billing/verify, where the server
 * checks the signature. Only then is access granted.
 *
 * Without a payment provider (the server's PAYMENT_GATEWAY=manual) the order carries `manual` details instead: the
 * customer sends the money by UPI or bank transfer, types the reference, and an administrator confirms it.
 *
 * Resolves to { status: 'paid', order } | { status: 'pending', order } | { status: 'cancelled' } | { status: 'failed', message }.
 */
import { api } from '../../core/api.js';
import { config } from '../../core/config.js';
import { register } from '../../core/events.js';
import { html } from '../../core/html.js';
import { inr } from '../../core/format.js';
import { field } from '../../ui/forms.js';
import { closeModal, openModal } from '../../ui/modal.js';
import { submitForm } from '../../ui/submit.js';
import { toast } from '../../ui/toast.js';

let sdk = null;

function loadCheckout() {
  if (window.Razorpay) return Promise.resolve();
  if (!sdk) {
    sdk = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = config.razorpayCheckoutUrl;
      script.async = true;
      script.onload = resolve;
      script.onerror = () => {
        sdk = null;
        reject(new Error('The payment window could not be loaded. Check your connection and try again.'));
      };
      document.head.append(script);
    });
  }
  return sdk;
}

let manualJob = null;

/** Show where to send the money and collect the transfer reference. Also used to resume an unpaid order. */
export function payManually(order, description = 'your order') {
  return new Promise((resolve) => {
    manualJob = { order, resolve, done: false };
    const m = order.manual;
    openModal({
      title: 'Pay by UPI or bank transfer',
      onClose: () => {
        if (manualJob && !manualJob.done) manualJob.resolve({ status: 'cancelled' });
        manualJob = null;
      },
      body: html`<p>Send exactly <b>${inr(order.total_paise)}</b> for ${description}.</p>
        <div class="card flat mt-s"><div class="sm mu">UPI ID</div><div><b id="pay-upi">${m.upi_id}</b></div><div class="sm mu mt-s">Payee</div><div><b>${m.payee}</b></div></div>
        <p class="sm mt-s">${m.instructions}</p>
        <form id="manual-pay" data-submit="pay.manual.send" class="mt-s" novalidate>
          ${field('pay-ref', 'UPI / bank reference (UTR)', { name: 'reference', minlen: 6, maxlength: 40, ph: '12-digit UTR from your payment app', help: 'You find it in your payment app under the transaction details.' })}</form>`,
      foot: html`<button type="button" class="btn ghost" data-act="modal.close">I will pay later</button><button type="submit" form="manual-pay" class="btn">I have paid</button>`,
    });
  });
}

register({
  'pay.manual.send': (form) => submitForm(form, async ({ reference }) => {
    const job = manualJob;
    if (!job) return;
    const { order } = await api.post(`/billing/orders/${job.order.id}/reference`, { body: { reference: reference.trim() } });
    job.done = true;
    closeModal(false);
    job.resolve({ status: 'pending', order });
    toast('Thank you. We will confirm your payment shortly.', 'check');
  }, { onError: (error) => toast(error.message, 'alert') }),
});

export async function payOrder(order, { description, prefill = {} } = {}) {
  if (order.manual) return payManually(order, description);
  // Development gateway: there is no Razorpay order, so the server confirms it directly (disabled in production).
  if (!order.key_id || String(order.gateway_order_id).startsWith('order_fake_')) {
    const result = await api.post('/billing/dev/confirm', { body: { order_id: order.id } });
    return { status: 'paid', order: result.order };
  }
  await loadCheckout();
  return new Promise((resolve, reject) => {
    let failure = null;
    const checkout = new window.Razorpay({
      key: order.key_id,
      order_id: order.gateway_order_id,
      amount: order.total_paise,
      currency: 'INR',
      name: config.brandName,
      description,
      prefill,
      theme: { color: '#4F46E5' },
      handler: async (response) => {
        try {
          const verified = await api.post('/billing/verify', {
            body: {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            },
          });
          resolve({ status: 'paid', order: verified.order });
        } catch (error) {
          reject(error);
        }
      },
      modal: { ondismiss: () => resolve(failure ? { status: 'failed', message: failure } : { status: 'cancelled' }) },
    });
    checkout.on('payment.failed', (event) => {
      failure = event?.error?.description || 'The payment did not go through.';
    });
    checkout.open();
  });
}
